import React, { useState, useMemo } from 'react';
import { useProductPriceAdvantages } from '@/hooks/use-product-rankings';
import { useBenchmarkProducts } from '@/hooks/use-benchmark-data';
import { ProductRankingFilters } from '@/services/benchmark/types';
import { rankProducts } from '@/services/benchmark/productRanking';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Download, TrendingDown, TrendingUp, Trophy, Award, Medal } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import * as XLSX from 'xlsx';

interface ProductPriceAdvantageRankingProps {
  rankingType: 'distribution-center' | 'supermarket' | 'overall' | 'consistency' | 'volume';
  dateFrom: string;
  dateTo: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  comparisonType: 'min' | 'avg';
}

const RANKING_CONFIGS = {
  'distribution-center': {
    marketGroup: 'distribution-center',
    title: 'Distribution Center Rankings',
  },
  'supermarket': {
    marketGroup: 'supermarket',
    title: 'Supermarket Rankings',
  },
  'overall': {
    marketGroup: 'overall',
    title: 'Overall Market Rankings',
  },
  'consistency': {
    marketGroup: 'consistency',
    title: 'Consistency Rankings',
  },
  'volume': {
    marketGroup: 'volume',
    title: 'Volume Opportunity Rankings',
  },
};

const SORT_OPTIONS = [
  { value: 'avgDiscountPercentage', label: 'Best Average Discount' },
  { value: 'maxDiscountPercentage', label: 'Highest Max Discount' },
  { value: 'consistencyScore', label: 'Most Consistent' },
  { value: 'percentDaysCheaper', label: 'Most Days Cheaper' },
  { value: 'avgAbsoluteSavings', label: 'Largest Absolute Savings' },
];

const ProductPriceAdvantageRanking: React.FC<ProductPriceAdvantageRankingProps> = ({
  rankingType,
  dateFrom,
  dateTo,
  frequency,
  comparisonType,
}) => {
  const { toast } = useToast();
  const { data: allProducts = [] } = useBenchmarkProducts();
  const config = RANKING_CONFIGS[rankingType];
  const [sortBy, setSortBy] = useState(
    rankingType === 'consistency' ? 'consistencyScore' : 
    rankingType === 'volume' ? 'avgAbsoluteSavings' : 
    'avgDiscountPercentage'
  );

  const filters: ProductRankingFilters = {
    products: allProducts,
    marketGroup: config.marketGroup,
    dateFrom,
    dateTo,
    frequency,
    comparisonType,
  };

  const { data: rankings = [], isLoading } = useProductPriceAdvantages(filters);

  const sortedRankings = useMemo(() => {
    return rankProducts(rankings, sortBy);
  }, [rankings, sortBy]);

  const summary = useMemo(() => {
    if (sortedRankings.length === 0) return null;

    const productsWhereCheaper = sortedRankings.filter(r => r.avgDiscountPercentage < 0).length;
    const avgAdvantage = sortedRankings.reduce((sum, r) => sum + r.avgDiscountPercentage, 0) / sortedRankings.length;
    const bestProduct = sortedRankings[0];

    return {
      total: sortedRankings.length,
      cheaper: productsWhereCheaper,
      avgAdvantage,
      bestProduct,
    };
  }, [sortedRankings]);

  const handleExport = () => {
    if (sortedRankings.length === 0) {
      toast({
        title: 'No data to export',
        description: 'Please wait for data to load',
        variant: 'destructive',
      });
      return;
    }

    const worksheetData = sortedRankings.map((item, index) => ({
      'Rank': index + 1,
      'Product': item.productName,
      'ChipChip Avg (Br)': item.avgChipchipPrice.toFixed(2),
      'Market Avg (Br)': item.avgMarketPrice.toFixed(2),
      'Avg Discount (%)': item.avgDiscountPercentage.toFixed(2),
      'Max Discount (%)': item.maxDiscountPercentage.toFixed(2),
      'Days Cheaper': item.daysChipchipCheaper,
      'Total Days': item.totalDays,
      '% Days Cheaper': item.percentDaysCheaper.toFixed(1),
      'Avg Savings (Br)': item.avgAbsoluteSavings.toFixed(2),
      'Consistency Score': item.consistencyScore.toFixed(0),
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Rankings');

    XLSX.writeFile(workbook, `Product_Price_Rankings_${config.title.replace(/\s+/g, '_')}_${dateFrom}_to_${dateTo}.xlsx`);

    toast({
      title: 'Export successful',
      description: 'Rankings have been exported to Excel',
    });
  };

  const getMedalIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Award className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-muted-foreground">{rank}</span>;
  };

  const getDiscountBadge = (discount: number) => {
    if (discount < -10) return <Badge className="bg-green-600">Excellent</Badge>;
    if (discount < -5) return <Badge className="bg-green-500">Good</Badge>;
    if (discount < 0) return <Badge className="bg-blue-500">Better</Badge>;
    if (discount < 5) return <Badge variant="secondary">Similar</Badge>;
    return <Badge variant="destructive">More Expensive</Badge>;
  };

  const getConsistencyBar = (score: number) => {
    const width = Math.min(100, Math.max(0, score));
    const color = score > 70 ? 'bg-green-500' : score > 40 ? 'bg-yellow-500' : 'bg-red-500';
    
    return (
      <div className="w-full bg-muted rounded-full h-2">
        <div className={`h-2 rounded-full ${color}`} style={{ width: `${width}%` }} />
      </div>
    );
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{config.title}</h2>
          <p className="text-muted-foreground">ChipChip price advantage analysis</p>
        </div>
        <Button onClick={handleExport} disabled={isLoading || sortedRankings.length === 0}>
          <Download className="w-4 h-4 mr-2" />
          Export to Excel
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Products</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">ChipChip Cheaper</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{summary.cheaper}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Avg Advantage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold flex items-center">
                {summary.avgAdvantage < 0 ? (
                  <TrendingDown className="w-5 h-5 text-green-600 mr-1" />
                ) : (
                  <TrendingUp className="w-5 h-5 text-red-600 mr-1" />
                )}
                {Math.abs(summary.avgAdvantage).toFixed(1)}%
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Best Product</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-semibold truncate">{summary.bestProduct.productName}</div>
              <div className="text-sm text-green-600">{summary.bestProduct.avgDiscountPercentage.toFixed(1)}% cheaper</div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex gap-4">
        <div className="flex-1">
          <label className="text-sm font-medium mb-2 block">Sort By</label>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Loading rankings...</div>
          ) : sortedRankings.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No data available for the selected filters</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">Rank</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Product</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">ChipChip Avg</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Market Avg</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Avg Discount</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Max Discount</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Days Cheaper</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">% Days</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Savings</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Consistency</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sortedRankings.map((item, index) => (
                    <tr key={item.productName} className={item.avgDiscountPercentage < 0 ? 'bg-green-50/50' : ''}>
                      <td className="px-4 py-3 text-center font-medium">
                        {getMedalIcon(index + 1)}
                      </td>
                      <td className="px-4 py-3 font-medium">{item.productName}</td>
                      <td className="px-4 py-3 text-right">{item.avgChipchipPrice.toFixed(2)} Br</td>
                      <td className="px-4 py-3 text-right">{item.avgMarketPrice.toFixed(2)} Br</td>
                      <td className="px-4 py-3 text-right font-semibold">
                        <span className={item.avgDiscountPercentage < 0 ? 'text-green-600' : 'text-red-600'}>
                          {item.avgDiscountPercentage.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={item.maxDiscountPercentage < 0 ? 'text-green-600' : 'text-red-600'}>
                          {item.maxDiscountPercentage.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.daysChipchipCheaper}/{item.totalDays}
                      </td>
                      <td className="px-4 py-3 text-right">{item.percentDaysCheaper.toFixed(0)}%</td>
                      <td className="px-4 py-3 text-right">
                        <span className={item.avgAbsoluteSavings > 0 ? 'text-green-600' : 'text-red-600'}>
                          {item.avgAbsoluteSavings.toFixed(2)} Br
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getConsistencyBar(item.consistencyScore)}
                          <span className="text-xs text-muted-foreground">{item.consistencyScore.toFixed(0)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{getDiscountBadge(item.avgDiscountPercentage)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductPriceAdvantageRanking;
